import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { useAuth } from '../hooks/useAuth';
import { User } from '../types';

const Users = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [editingSlackId, setEditingSlackId] = useState<number | null>(null);
  const [slackIdDraft, setSlackIdDraft] = useState('');
  const [returnFocusTo, setReturnFocusTo] = useState<number | null>(null);
  const editButtonRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const fetchUsers = async () => {
    setIsLoading(true);
    try {
      const res = await fetchWithAuth('/users');
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data.users);
      setError(null);
    } catch (err) {
      setError('Something went wrong while fetching users.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const toggleActive = async (target: User) => {
    const action = target.active ? 'deactivate' : 'activate';

    if (target.active) {
      const confirmed = window.confirm(
        `Deactivate ${target.email}? They will be signed out immediately and can no longer log in or receive event notifications.`
      );
      if (!confirmed) return;
    }

    setUpdatingId(target.id);
    try {
      const res = await fetchWithAuth(`/users/${target.id}/${action}`, { method: 'PATCH' });
      if (!res.ok) return; // fetchWithAuth surfaces the error toast
      const data = await res.json();
      setUsers((prev) => prev.map((u) => (u.id === data.user.id ? data.user : u)));
      toast.success(target.active ? 'User deactivated' : 'User activated');
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  useEffect(() => {
    if (returnFocusTo === null) return;
    editButtonRefs.current[returnFocusTo]?.focus();
    setReturnFocusTo(null);
  }, [returnFocusTo]);

  const stopEditingSlackId = (target: User) => {
    setEditingSlackId(null);
    setReturnFocusTo(target.id);
  };

  const startEditingSlackId = (target: User) => {
    setEditingSlackId(target.id);
    setSlackIdDraft(target.slack_user_id ?? '');
  };

  const saveSlackId = async (target: User) => {
    setUpdatingId(target.id);
    try {
      const res = await fetchWithAuth(`/users/${target.id}/slack_user_id`, {
        method: 'PATCH',
        body: JSON.stringify({ slack_user_id: slackIdDraft }),
      });
      if (!res.ok) return;
      const data = await res.json();
      setUsers((prev) => prev.map((u) => (u.id === data.user.id ? data.user : u)));
      stopEditingSlackId(target);
      toast.success('Slack user ID saved');
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="px-4 md:px-6 py-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Users</h1>
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Deactivated users are signed out immediately and can no longer log in or receive event notifications.
      </p>
      {error && <div className="text-red-600 font-medium mb-4">{error}</div>}

      {isLoading && (
        <div className="w-full h-1 bg-gray-200 rounded overflow-hidden mb-2">
          <div className="h-full bg-blue-500 animate-pulse" style={{ width: '100%' }}></div>
        </div>
      )}

      <div className="overflow-x-auto min-h-[200px] pb-12">
        <table className="min-w-full bg-white border border-gray-200 rounded-md shadow-sm">
          <thead>
            <tr className="bg-gray-100 text-left text-sm font-medium text-gray-700">
              <th className="px-2 md:px-4 py-2">Email</th>
              <th className="px-2 md:px-4 py-2">Role</th>
              <th className="px-2 md:px-4 py-2">Slack User ID</th>
              <th className="px-2 md:px-4 py-2">Status</th>
              <th className="px-2 md:px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = u.id === currentUser?.id;
              return (
                <tr key={u.id} className="border-t border-gray-100 text-sm text-gray-800 hover:bg-yellow-100">
                  <td className="px-2 md:px-4 py-2">
                    {u.email}
                    {isSelf && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                  </td>
                  <td className="px-2 md:px-4 py-2 capitalize">{u.role}</td>
                  <td className="px-2 md:px-4 py-2">
                    {editingSlackId === u.id ? (
                      <form
                        className="flex flex-wrap items-center gap-2"
                        onSubmit={(e) => {
                          e.preventDefault();
                          saveSlackId(u);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape' && updatingId !== u.id) stopEditingSlackId(u);
                        }}
                      >
                        <input
                          type="text"
                          autoFocus
                          aria-label={`Slack user ID for ${u.email}`}
                          value={slackIdDraft}
                          onChange={(e) => setSlackIdDraft(e.target.value)}
                          placeholder="U0123ABCD"
                          className="w-28 sm:w-32 px-3 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          type="submit"
                          disabled={updatingId === u.id}
                          className="bg-black text-white px-3 py-1 rounded text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                        >
                          {updatingId === u.id ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={() => stopEditingSlackId(u)}
                          disabled={updatingId === u.id}
                          className="bg-gray-300 text-gray-700 px-3 py-1 rounded text-sm font-medium disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </form>
                    ) : (
                      <div className="flex items-center gap-2">
                        <span className={u.slack_user_id ? 'font-mono' : 'text-gray-500'}>
                          {u.slack_user_id || 'Not set'}
                        </span>
                        <button
                          ref={(el) => {
                            editButtonRefs.current[u.id] = el;
                          }}
                          onClick={() => startEditingSlackId(u)}
                          aria-label={`Edit Slack user ID for ${u.email}`}
                          className="text-sm text-blue-600 hover:text-blue-800 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 md:px-4 py-2">
                    <span
                      className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                        u.active ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {u.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-2 md:px-4 py-2">
                    {isSelf ? (
                      <span className="text-xs text-gray-400 whitespace-nowrap">—</span>
                    ) : u.active ? (
                      <button
                        onClick={() => toggleActive(u)}
                        disabled={updatingId === u.id}
                        className="bg-red-500 text-white px-3 py-1 rounded text-sm font-medium hover:bg-red-600 disabled:opacity-50"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        onClick={() => toggleActive(u)}
                        disabled={updatingId === u.id}
                        className="bg-black text-white px-3 py-1 rounded text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!isLoading && users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-sm">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Users;
